/**
 * 会员特权 Tab 组件
 */

import React from 'react';
import {
  Paper,
  Box,
  Typography,
  Grid,
  Chip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Button,
  useTheme,
  useMediaQuery,
  Divider,
  LinearProgress,
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Upgrade as UpgradeIcon,
  EmojiEvents as EmojiEventsIcon,
  AccessTime as AccessTimeIcon,
} from '@mui/icons-material';
import { MEMBERSHIP_LEVELS, getMembershipInfo, formatDate, daysBetween } from '../../services/mongoUserApi';
import TierCard from './TierCard';

const MembershipTab = ({ user }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  if (!user) return null;

  const userMembership = user.membership || {};
  const currentLevel = userMembership.level || 'free';
  const membershipInfo = getMembershipInfo(currentLevel);
  const privileges = membershipInfo.privileges || {};

  // 计算会员剩余天数
  const getRemainingDays = () => {
    if (currentLevel === 'free') return null;
    if (!userMembership.expires_at) return '永久';
    const days = daysBetween(userMembership.expires_at, new Date());
    return days;
  };

  const remainingDays = getRemainingDays();

  // 特权项配置
  const privilegeConfig = [
    {
      key: 'ad_free',
      label: '无广告观看',
      description: '观看视频时无广告干扰',
      icon: '🚫',
    },
    {
      key: 'download_enabled',
      label: '离线下载',
      description: '下载视频到本地观看',
      icon: '⬇️',
    },
    {
      key: 'max_watch_quality',
      label: '最高画质',
      description: '可观看的最高视频清晰度',
      icon: '📺',
      formatValue: (value) => value,
    },
    {
      key: 'max_watch_dramas',
      label: '同时观看剧集数',
      description: '可同时观看的剧集数量上限',
      icon: '🎬',
      formatValue: (value) => `${value} 部`,
    },
    {
      key: 'early_access_days',
      label: '提前看天数',
      description: '新剧上线后可提前观看的天数',
      icon: '⏰',
      formatValue: (value) => `${value} 天`,
    },
    {
      key: 'vip_service',
      label: 'VIP专属客服',
      description: '享受专属客服优先服务',
      icon: '👨‍💼',
    },
    {
      key: 'discount_rate',
      label: '消费折扣',
      description: '会员充值消费时的折扣比例',
      icon: '💰',
      formatValue: (value) => `${value}%`,
    },
  ];

  // 获取下一个等级
  const getNextLevel = () => {
    const levels = Object.keys(MEMBERSHIP_LEVELS);
    const currentIndex = levels.indexOf(currentLevel);
    if (currentIndex < levels.length - 1) {
      return {
        level: levels[currentIndex + 1],
        ...MEMBERSHIP_LEVELS[levels[currentIndex + 1]],
      };
    }
    return null;
  };

  const nextLevel = getNextLevel();

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      {/* === 等级体系 v3 (LevelUp 2.0) - 月度等级 === */}
      <TierCard userId={user?._id || user?.id} />

      {/* 当前会员状态卡片 */}
      <Paper
        sx={{
          borderRadius: 2,
          overflow: 'hidden',
          background: `linear-gradient(135deg, ${membershipInfo.color}22 0%, ${membershipInfo.color}44 100%)`,
        }}
      >
        <Box
          sx={{
            p: isMobile ? 2 : 3,
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            borderBottom: `1px solid ${theme.palette.divider}`,
          }}
        >
          <EmojiEventsIcon
            sx={{
              fontSize: 32,
              color: membershipInfo.color,
            }}
          />
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            当前会员状态
          </Typography>
        </Box>

        <Box sx={{ p: isMobile ? 2 : 3 }}>
          <Grid container spacing={isMobile ? 2 : 3}>
            {/* 会员等级 */}
            <Grid item xs={12} sm={6}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                }}
              >
                <Typography variant="h3" sx={{ fontSize: 48 }}>
                  {membershipInfo.icon}
                </Typography>
                <Box>
                  <Typography
                    variant="h5"
                    sx={{
                      fontWeight: 700,
                      color: membershipInfo.color,
                    }}
                  >
                    {membershipInfo.nameCN}
                  </Typography>
                  {currentLevel !== 'free' && (
                    <Typography variant="caption" color="text.secondary">
                      Member ID: {user.user_id.slice(-8).toUpperCase()}
                    </Typography>
                  )}
                </Box>
              </Box>
            </Grid>

            {/* 会员有效期 */}
            <Grid item xs={12} sm={6}>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  会员有效期
                </Typography>
                {currentLevel === 'free' ? (
                  <Typography variant="body1" sx={{ mt: 0.5 }}>
                    永久免费会员
                  </Typography>
                ) : (
                  <Box sx={{ mt: 0.5 }}>
                    <Typography variant="body1">
                      {formatDate(userMembership.start_at, 'YYYY-MM-DD')} ~{' '}
                      {formatDate(userMembership.expires_at, 'YYYY-MM-DD')}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                      <AccessTimeIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                      <Typography variant="body2" color="text.secondary">
                        剩余 <strong>{remainingDays}</strong> 天
                      </Typography>
                    </Box>
                  </Box>
                )}
              </Box>
            </Grid>
          </Grid>
        </Box>
      </Paper>

      {/* 会员特权列表 */}
      <Paper sx={{ borderRadius: 2, overflow: 'hidden' }}>
        <Box
          sx={{
            p: isMobile ? 2 : 3,
            borderBottom: `1px solid ${theme.palette.divider}`,
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            我的特权
          </Typography>
        </Box>

        <Box sx={{ p: isMobile ? 2 : 3 }}>
          <Grid container spacing={2}>
            {privilegeConfig.map((item) => {
              const value = privileges[item.key];
              const isEnabled = typeof value === 'boolean' ? value : value > 0;
              const displayValue = item.formatValue
                ? item.formatValue(value)
                : typeof value === 'boolean'
                ? value
                  ? '已开启'
                  : '未开启'
                : value;

              return (
                <Grid item xs={12} sm={6} md={4} key={item.key}>
                  <Paper
                    sx={{
                      p: 2,
                      height: '100%',
                      backgroundColor: isEnabled
                        ? membershipInfo.bgColor
                        : theme.palette.action.hover,
                      border: isEnabled
                        ? `1px solid ${membershipInfo.color}44`
                        : `1px solid ${theme.palette.divider}`,
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        transform: 'translateY(-2px)',
                        boxShadow: 2,
                      },
                    }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        mb: 1,
                      }}
                    >
                      <Typography sx={{ fontSize: 20 }}>{item.icon}</Typography>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                        {item.label}
                      </Typography>
                    </Box>
                    <Typography variant="caption" color="text.secondary">
                      {item.description}
                    </Typography>
                    <Box sx={{ mt: 1.5 }}>
                      {isEnabled ? (
                        <Chip
                          label={displayValue}
                          size="small"
                          color="success"
                          icon={<CheckCircleIcon sx={{ fontSize: 14 }} />}
                        />
                      ) : (
                        <Chip
                          label="未开通"
                          size="small"
                          color="default"
                          icon={<CancelIcon sx={{ fontSize: 14 }} />}
                        />
                      )}
                    </Box>
                  </Paper>
                </Grid>
              );
            })}
          </Grid>
        </Box>
      </Paper>

      {/* 升级提示卡片 */}
      {nextLevel && (
        <Paper sx={{ borderRadius: 2, overflow: 'hidden' }}>
          <Box
            sx={{
              p: isMobile ? 2 : 3,
              borderBottom: `1px solid ${theme.palette.divider}`,
            }}
          >
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              升级到 {nextLevel.nameCN}
            </Typography>
          </Box>

          <Box sx={{ p: isMobile ? 2 : 3 }}>
            <Grid container spacing={3} alignItems="center">
              <Grid item xs={12} md={8}>
                <Box sx={{ mb: 2 }}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      mb: 1,
                    }}
                  >
                    <Typography variant="h2" sx={{ fontSize: 36 }}>
                      {nextLevel.icon}
                    </Typography>
                    <Typography
                      variant="h5"
                      sx={{
                        fontWeight: 700,
                        color: nextLevel.color,
                      }}
                    >
                      {nextLevel.nameCN}
                    </Typography>
                  </Box>
                  <Typography variant="body2" color="text.secondary">
                    升级即可享受更多会员特权：
                    {nextLevel.privileges.ad_free && ' 无广告观看、'}
                    {nextLevel.privileges.download_enabled && ' 离线下载、'}
                    {nextLevel.privileges.max_watch_quality && ` ${nextLevel.privileges.max_watch_quality}画质、`}
                    等更多特权！
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} md={4} sx={{ textAlign: { xs: 'left', md: 'right' } }}>
                <Button
                  variant="contained"
                  size="large"
                  startIcon={<UpgradeIcon />}
                  sx={{
                    backgroundColor: nextLevel.color,
                    '&:hover': {
                      backgroundColor: nextLevel.color,
                      opacity: 0.9,
                    },
                  }}
                >
                  立即升级
                </Button>
              </Grid>
            </Grid>

            <Divider sx={{ my: 2 }} />

            {/* 等级进度条 */}
            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                会员成长进度
              </Typography>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2,
                }}
              >
                <Typography variant="body2">
                  {membershipInfo.icon} {membershipInfo.nameCN}
                </Typography>
                <Box sx={{ flexGrow: 1 }}>
                  <LinearProgress
                    variant="determinate"
                    value={50}
                    sx={{
                      height: 8,
                      borderRadius: 4,
                      backgroundColor: theme.palette.action.hover,
                      '& .MuiLinearProgress-bar': {
                        backgroundColor: nextLevel.color,
                      },
                    }}
                  />
                </Box>
                <Typography variant="body2">
                  {nextLevel.icon} {nextLevel.nameCN}
                </Typography>
              </Box>
            </Box>
          </Box>
        </Paper>
      )}

      {/* 会员等级说明 */}
      <Paper sx={{ borderRadius: 2, overflow: 'hidden' }}>
        <Box
          sx={{
            p: isMobile ? 2 : 3,
            borderBottom: `1px solid ${theme.palette.divider}`,
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            全部会员等级
          </Typography>
        </Box>

        <Box sx={{ p: isMobile ? 2 : 3 }}>
          <List disablePadding>
            {Object.entries(MEMBERSHIP_LEVELS).map(([levelKey, levelInfo], index) => {
              const isCurrentLevel = levelKey === currentLevel;
              return (
                <React.Fragment key={levelKey}>
                  {index > 0 && <Divider component="li" />}
                  <ListItem
                    sx={{
                      py: 1.5,
                      borderRadius: 1,
                      backgroundColor: isCurrentLevel ? levelInfo.bgColor : 'transparent',
                      border: isCurrentLevel ? `1px solid ${levelInfo.color}44` : 'none',
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 40 }}>
                      <Typography sx={{ fontSize: 24 }}>{levelInfo.icon}</Typography>
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                            {levelInfo.nameCN}
                          </Typography>
                          {isCurrentLevel && (
                            <Chip
                              label="当前等级"
                              size="small"
                              color="primary"
                              sx={{ height: 20, fontSize: 10 }}
                            />
                          )}
                        </Box>
                      }
                      secondary={`可观看 ${levelInfo.privileges.max_watch_dramas} 部剧集，最高 ${levelInfo.privileges.max_watch_quality} 画质`}
                    />
                  </ListItem>
                </React.Fragment>
              );
            })}
          </List>
        </Box>
      </Paper>
    </Box>
  );
};

export default MembershipTab;
